/* eslint-disable jsx-a11y/media-has-caption */
import React from "react";
import { Link } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  AlertData,
  Order,
  MutedAlertSites,
  MutedAlertCameras,
} from "./AlertsUtils/alertsInterfaces";
import {
  getComparator,
  setDetectionTypeLower,
  stableSort,
} from "./AlertsUtils/alertsTableFunctions";
import {
  filterAlertsBeforeVlm,
  getNavigableAlerts,
} from "./AlertsUtils/getNavigableAlerts";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { EnhancedAlertsTableHead } from "./EnhancedAlertsTableHead";
import { handleRowSelected } from "./AlertsUtils/handleRowSelected";
import { canSetAlertOutcome } from "./AlertsUtils/alertOutcomePermission";
import { createDetctionTypeIcon } from "./AlertsUtils/createDetctionTypeIcon";
import { Checkbox, FormControlLabel, TableHead, Tooltip } from "@mui/material";
import { FilterMenu } from "./FilterMenu";
import { handleReorderIndex } from "./AlertsUtils/handleReorderIndex";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { DetectionTypeFilter } from "./DetectionTypeFilter";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { saveAs } from "file-saver";
import { OutcomeTypeFilter } from "./OutcomeTypeFilter";
import LoopIcon from "@mui/icons-material/Loop";
import SyncDisabledIcon from "@mui/icons-material/SyncDisabled";
import Countdown from "./Countdown";
import { useAppStore } from "../../../appStore";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useAlertsStore } from "./AlertsStore/alertsStore";
import { useAlertsFetchErrorStore } from "./AlertsStore/alertsFetchErrorStore";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { isAlertMuted, hideMutedFromView } from "./AlertsUtils/isAlertMuted";
import { getAlertsWithQuerry } from "./AlertsUtils/getAlertsWithQuerry";
import { yyyymmddGenerator } from "../Analytics/AnalyticsUtils/yyyymmddGenerator";
import { updateSearchParamValues } from "../../utils/updateSearchParamValues";
import { getAlertsDownloadURL } from "./AlertsUtils/getAlertsDownloadURL";
import {
  ALERT_RETENTION_DAYS,
  isArchivedRange,
} from "./AlertsUtils/archivedRange";
import { getVideoUrlCacheKey } from "./AlertsUtils/videoUrlCache";
import { alertsTableFilterRowClassName } from "./AlertsUtils/alertsFilterBar";
import {
  applyVlmFilter,
  getVlmFilterCount,
  isAlertWithheldFromReceiver,
  getVlmQueueCounts,
  hasDoubleTakeInAlerts,
  VLM_FILTER_LABELS,
  VLM_FILTER_ORDER,
  VlmFilterType,
} from "./AlertsUtils/vlmFilters";
import { VlmVerdictCell, VlmVerdictChip } from "./VlmVerdictIndicator";
import {
  alertsTableSeenColumnSx,
  alertsTableSiteCameraCellSx,
} from "./AlertsUtils/alertsTableResize";
import {
  ALERTS_ROWS_PER_PAGE_OPTIONS,
  readStoredAlertsRowsPerPage,
  storeAlertsRowsPerPage,
} from "./AlertsUtils/alertsRowsPerPage";
import { AlertsDensityToggle } from "./AlertsDensityToggle";
import { AlertTypeChip } from "./AlertTypeChip";
import { TruncatedTextWithTooltip } from "./AlertsUtils/TruncatedTextWithTooltip";
import { AlertsTableDensity } from "./AlertsUtils/alertsDensity";
import {
  formatWindowSince,
  ScopeGroup,
  scopeEmptyMessage,
} from "./AlertsUtils/alertScope";
import { useAlertScopeView } from "./AlertsUtils/useAlertScopeView";
import {
  countUnresolvedAlerts,
  getAlertTypeStripeColor,
  getCondensedWindowLabel,
  handleCreateAlertTypeNoUnderscore,
  isAlertResolved,
} from "./AlertsUtils/alertTypeStyles";

export { handleCreateAlertTypeNoUnderscore, setDetectionTypeLower };

// Function to swap MM/DD in a date string
const formatDateBasedOnPreference = (
  dateString: string,
  isMMDDYYYY: boolean
) => {
  if (isMMDDYYYY) return dateString; // Return original format if MM/DD/YYYY is preferred

  // Parse the date string - expected format: "MM/DD/YYYY, HH:MM:SS AM/PM"
  const parts = dateString.split(", ");
  if (parts.length !== 2) return dateString; // Return original if format is unexpected

  const datePart = parts[0];
  const timePart = parts[1];

  // Split the date part by "/"
  const dateSections = datePart.split("/");
  if (dateSections.length !== 3) return dateString; // Return original if format is unexpected

  // Swap month and day
  const [month, day, year] = dateSections;
  const swappedDatePart = `${day}/${month}/${year}`;

  // Return the swapped date with the original time
  return `${swappedDatePart}, ${timePart}`;
};

/**
 * Scroll `container` the minimum distance needed to show `rowEl` fully,
 * keeping it clear of the sticky table header. No-op when already visible,
 * so it never fights a user who is looking at the row.
 */
/** Slack-style "jump to newest": show when the list can scroll and isn't at top. */
export const shouldShowNewestAlertsButton = (
  scrollTop: number,
  scrollHeight: number,
  clientHeight: number,
  scrollAwayThresholdPx = 8
): boolean => {
  const canScroll = scrollHeight > clientHeight + 1;
  return canScroll && scrollTop > scrollAwayThresholdPx;
};

export const scrollAlertRowIntoView = (
  container: HTMLElement,
  rowEl: HTMLElement
) => {
  const containerRect = container.getBoundingClientRect();
  const rowRect = rowEl.getBoundingClientRect();
  const headerHeight =
    container.querySelector("thead")?.getBoundingClientRect().height ?? 0;
  const visibleTop = containerRect.top + headerHeight;
  if (rowRect.top < visibleTop) {
    container.scrollTop -= visibleTop - rowRect.top;
  } else if (rowRect.bottom > containerRect.bottom) {
    container.scrollTop += rowRect.bottom - containerRect.bottom;
  }
};

const compactTablePaginationSx = {
  "& .MuiTablePagination-toolbar": {
    minHeight: "36px",
    paddingLeft: "0",
    paddingRight: "4px",
    // Let "Rows per page" drop onto its own line rather than pushing the
    // page arrows past the card edge when the table column is narrow.
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  "& .MuiTablePagination-spacer": {
    display: "none",
  },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select":
    {
      fontSize: "12px",
      fontFamily: "Mulish",
    },
};

/**
 * Tile colors per bucket (ENG-1756): the working queue is the orange primary,
 * All is neutral, and the two verdict buckets reuse the Likely TP green /
 * Likely FP red (the False outcome button's red) that the row indicators use.
 */
const VLM_TILE_PALETTE: Record<VlmFilterType, { idle: string; active: string }> = {
  "Filtered Queue": {
    idle: "border-actuate-orange/35 bg-actuate-orange/10 text-actuate-orange",
    active: "border-actuate-orange bg-actuate-orange text-white",
  },
  All: {
    idle: "border-[#6B7280]/35 bg-[#6B7280]/10 text-actuate-blue",
    active: "border-[#6B7280] bg-[#6B7280] text-white",
  },
  "Likely TP": {
    idle: "border-[#2E7D32]/35 bg-[#2E7D32]/10 text-[#2E7D32]",
    active: "border-[#2E7D32] bg-[#2E7D32] text-white",
  },
  "Likely FP": {
    idle: "border-[#D32F2F]/35 bg-[#D32F2F]/10 text-[#D32F2F]",
    active: "border-[#D32F2F] bg-[#D32F2F] text-white",
  },
  // ENG-1599: alerts the connector withheld from Immix/Patriot on the verdict.
  Withheld: {
    idle: "border-[#7d2e72]/35 bg-[#7d2e72]/10 text-[#7d2e72]",
    active: "border-[#7d2e72] bg-[#7d2e72] text-white",
  },
};

export const AlertsTable = ({
  tableBodyHeight = "550px",
  fillAvailableHeight = false,
  selected,
  alertData,
  setAlertData,
  setSelected,
  selectedRow,
  setSelectedRow,
  setSelectedButtonsFilled,
  setSeeMoreURL,
  navigate,
  setMP4URL,
  page,
  setPage,
  pageSyncedKeyRef,
  userFilters,
  setFetchingFilteredData,
  setUserFilters,
  setThumbnailVideos,
  setDateOutsideRange,
  setVideoClipLoading,
  setAlertsToDisplay,
  setPageLoading,
  setSelectedAlertCameraInfo,
  setIgnoreZonesSelectedAlert,
  setAlertsIndexOrder,
  setRefreshInterval,
  currentName,
  setCurrentName,
  pageLoadedNumber,
  setPageLoadedNumber,
  setRefreshingData,
  cameraOptions,
  siteOptions,
  scrollToTop,
  refreshingToken,
  setRefreshingToken,
  setAlertLabels,
  alertLabels,
  detectionType,
  setDetectionType,
  setPeriodHours,
  setQueryLimitHit,
  searchParams,
  setSearchParams,
  refreshInterval,
  outcomeFilterType,
  setOutcomeFilterType,
  setBackArrowDisabled,
  setForwardArrowDisabled,
  setShowRefreshButton,
  setRowsSortable,
  rowsSortable,
  setAlertsRefreshing,
  alertsRefreshing,
  getCurrentSelectedRowKey,
  setVideoProcessing,
  useAsyncGeneration,
  setSelectedRowRef,
  vlmFilterType,
  setVlmFilterType,
  tableDensity = "detailed",
  onDensityChange,
  periodHours = 2,
  filtersApplied = false,
  parentAccounts = [],
  onTableSortChange,
}: {
  tableBodyHeight?: string;
  fillAvailableHeight?: boolean;
  selected: any;
  alertData: AlertData[];
  setAlertData: any;
  setSelected: any;
  selectedRow: any;
  setSelectedRow: any;
  setSelectedButtonsFilled: any;
  setSeeMoreURL: any;
  navigate: any;
  setMP4URL: any;
  page: number;
  setPage: any;
  /**
   * Owned by the parent, like `page`. Remembers which selection the page was
   * last synced to, so it has to survive the remount a background refresh
   * causes. Optional: without it the memory is local to this mount.
   */
  pageSyncedKeyRef?: React.MutableRefObject<string | null>;
  userFilters: any;
  setFetchingFilteredData: any;
  setUserFilters: any;
  setThumbnailVideos: any;
  setDateOutsideRange: any;
  setVideoClipLoading: any;
  setAlertsToDisplay: any;
  setPageLoading: any;
  setSelectedAlertCameraInfo: any;
  setIgnoreZonesSelectedAlert: any;
  setAlertsIndexOrder: any;
  setRefreshInterval: any;
  currentName: any;
  setCurrentName: any;
  pageLoadedNumber: number;
  setPageLoadedNumber: any;
  setRefreshingData: any;
  cameraOptions: any;
  siteOptions: any;
  scrollToTop: any;
  refreshingToken: boolean;
  setRefreshingToken: any;
  setAlertLabels: any;
  alertLabels: string[];
  detectionType: string;
  setDetectionType: any;
  setPeriodHours: any;
  setQueryLimitHit: any;
  searchParams: any;
  setSearchParams: any;
  refreshInterval: any;
  outcomeFilterType: string;
  setOutcomeFilterType: any;
  setBackArrowDisabled: any;
  setForwardArrowDisabled: any;
  setShowRefreshButton: any;
  setRowsSortable: any;
  rowsSortable: any;
  setAlertsRefreshing: any;
  alertsRefreshing: boolean;
  getCurrentSelectedRowKey?: () => string | null;
  setVideoProcessing: any;
  useAsyncGeneration: boolean;
  setSelectedRowRef: (row: any) => void;
  vlmFilterType: VlmFilterType;
  setVlmFilterType: React.Dispatch<React.SetStateAction<VlmFilterType>>;
  tableDensity?: AlertsTableDensity;
  onDensityChange?: (density: AlertsTableDensity) => void;
  periodHours?: number;
  // True once the first alerts fetch has completed (getAlerts sets it after
  // response handling) — gates the archived-range CTA so it can't flash while
  // alertData is empty during the initial load (ENG-1842).
  filtersApplied?: boolean;
  parentAccounts?: ScopeGroup[];
  onTableSortChange?: (order: Order, orderBy: string) => void;
}) => {
  const { flags } = useFeatureFlag();
  // The flag enables DoubleTake; the tiles, DT Verdict column and chips only
  // render when the alerts in view include a DoubleTake camera. Alerts.tsx
  // derives the same value, so the applied tile filter and the tiles agree.
  const showVlmFilters = React.useMemo(
    () => Boolean(flags.showVlmFilters) && hasDoubleTakeInAlerts(alertData),
    [flags.showVlmFilters, alertData]
  );
  // ENG-1842: archived-range reports need the whole backend chain live (Athena
  // workgroup + IAM + Glue mirror view + ATHENA_REPORTS_WORKGROUP on the pod).
  // Until then download_v2 takes the DynamoDB path for old ranges and returns a
  // headers-only CSV — worse than no button — so the notice ships dark behind
  // this flag and is flipped on per environment after backend activation.
  const showArchivedReports = Boolean(flags.showArchivedAlertReports);
  const showAlertsScopeContext = Boolean(flags.showAlertsScopeContext);
  const showTroubleshootingPage = Boolean(flags.showTroubleshootingPage);
  const appliedGroupId =
    showAlertsScopeContext && typeof searchParams?.get === "function"
      ? searchParams.get("group")
      : null;
  const { scope, other, minStartTimestamp } = useAlertScopeView({
    enabled: showAlertsScopeContext,
    parentAccounts,
    appliedGroupId,
  });
  const hasSiteOrCameraFilter = Boolean(
    (typeof searchParams?.get === "function" && searchParams.get("site")) ||
      (typeof searchParams?.get === "function" && searchParams.get("camera"))
  );
  const scopeEmptyCopy =
    showAlertsScopeContext && filtersApplied
      ? scopeEmptyMessage({
          narrower: Boolean(scope?.narrower),
          groupName: scope?.selected?.name ?? null,
          accountName: scope?.accountRoot?.name ?? null,
          sinceLabel: formatWindowSince({
            minStartTimestamp,
            startDate: userFilters?.start_date,
            startTime: userFilters?.start_time,
            periodHours: Number(periodHours) || 2,
          }),
          other,
          hasSiteOrCameraFilter,
        })
      : null;
  const showOutcomeFilter = currentName === "Resolved Alerts";

  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState("");
  // Lazy-read from storage: the parent remounts this table on every list
  // refresh, so plain useState(25) reset the user's choice (SUP-510).
  const [rowsPerPage, setRowsPerPage] = React.useState(
    readStoredAlertsRowsPerPage
  );
  const { dateMMDDYYYY, setDateMMDDYYYY, userPermissions } = useAppStore();
  const viewAlertMuting = userPermissions.alert_muting;
  const {
    mutedAlertSites,
    mutedAlertCameras,
    setSeeMutedSiteAndCamerasDialogOpen,
    handleSubmitMutedAlert,
  } = useAlertsStore();
  // Set by the alert fetchers when /monitoring-api/alert/ fails (SUP-445).
  const fetchError = useAlertsFetchErrorStore((state) => state.fetchError);
  const fetchErrorMessage = fetchError
    ? `Alerts could not be loaded${
        fetchError.status ? ` (HTTP ${fetchError.status})` : ""
      }. Try applying the filters again; if this keeps happening, contact support.`
    : null;

  const isCondensed = tableDensity === "condensed";
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [footerMenuAnchor, setFooterMenuAnchor] =
    React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const footerMenuOpen = Boolean(footerMenuAnchor);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Stop event propagation to prevent table sorting
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Stop event propagation to prevent table sorting
    setAnchorEl(null);
  };

  const [count, setCount] = React.useState(alertData.length);
  // ENG-1842 review: the fetched report URL is keyed to the filters it was
  // fetched FOR — after a range/site/camera change the button must fetch again,
  // never hand back the previous filter set's CSV.
  const [fetchedReport, setFetchedReport] = React.useState<{
    key: string;
    url: string;
  } | null>(null);
  const [isFetchingReport, setIsFetchingReport] = React.useState(false);
  const reportAbortRef = React.useRef<AbortController | null>(null);
  const mountedRef = React.useRef(true);

  const reportKey = JSON.stringify([
    userFilters.parent_group,
    userFilters.site,
    userFilters.camera,
    yyyymmddGenerator(userFilters.start_date),
    yyyymmddGenerator(userFilters.end_date),
    userFilters.start_time,
    userFilters.end_time,
  ]);
  const reportDownloadUrl =
    fetchedReport?.key === reportKey ? fetchedReport.url : null;
  // Mirrors getAlertsDownloadURL's own "send camera_id" condition: when a
  // camera filter would be sent, an archived-range report is a guaranteed 400
  // (the mirror has no camera ids), so the CTA is replaced with an explanation.
  const reportCameraFilterActive =
    Boolean(userFilters.camera) &&
    String(userFilters.camera) !== "undefined" &&
    String(userFilters.camera) !== "";

  React.useEffect(() => {
    // Filter change (or unmount, via the effect below): abort any in-flight
    // report poll — its URL would belong to a filter set the user has left.
    // The server-side report still completes and is reused when re-requested
    // (the backend keys the Athena query on the request parameters).
    return () => {
      reportAbortRef.current?.abort();
      reportAbortRef.current = null;
    };
  }, [reportKey]);

  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof AlertData
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(nextRowsPerPage);
    storeAlertsRowsPerPage(nextRowsPerPage);
    setPage(0);
  };

  const theme = createTheme({
    palette: {
      primary: {
        main: "#FF8400",
      },
    },
  });

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - alertData.length) : 0;

  /**
   * Every row filter except DoubleTake: muted, Type and Outcome.
   *
   * This is the input both the table rows and the DoubleTake tile counts are
   * built from, so a tile count and the rows that tile shows can never be
   * drawn from different populations (ENG-1756). Counting before Type/Outcome
   * let a tile read "Likely TP 12" while the table showed none.
   */
  const getRowsBeforeVlmFilter = (
    alertData: any,
    detectionType: string,
    outcomeFilterType: string
  ) =>
    filterAlertsBeforeVlm({
      alertData,
      detectionType,
      outcomeFilterType,
      mutedAlertSites,
      mutedAlertCameras,
      filterType: userFilters?.filter_type,
    });

  const checkRowsForFilter = (
    alertData: any,
    detectionType: string,
    outcomeFilterType: string,
    currentVlmFilterType: VlmFilterType
  ) => {
    const baseRows = getRowsBeforeVlmFilter(
      alertData,
      detectionType,
      outcomeFilterType
    );

    return showVlmFilters
      ? applyVlmFilter(baseRows, currentVlmFilterType)
      : baseRows;
  };

  const filteredRows = React.useMemo(
    () =>
      checkRowsForFilter(
        alertData,
        detectionType,
        outcomeFilterType,
        vlmFilterType
      ),
    [
      alertData,
      detectionType,
      outcomeFilterType,
      mutedAlertSites,
      mutedAlertCameras,
      showVlmFilters,
      vlmFilterType,
    ]
  );

  React.useEffect(() => {
    setCount(filteredRows.length);
  }, [filteredRows]);

  const sortedRows = React.useMemo(
    () =>
      getNavigableAlerts({
        alertData,
        detectionType,
        outcomeFilterType,
        vlmFilterType,
        showVlmFilters,
        mutedAlertSites,
        mutedAlertCameras,
        filterType: userFilters?.filter_type,
        order,
        orderBy,
      }),
    [
      alertData,
      detectionType,
      outcomeFilterType,
      vlmFilterType,
      showVlmFilters,
      mutedAlertSites,
      mutedAlertCameras,
      userFilters?.filter_type,
      order,
      orderBy,
    ]
  );
  const visibleRows = React.useMemo(
    () =>
      sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  // Follow the selection. Keyboard / arrow / auto-advance navigation changes
  // selectedRow without touching the table, and background refreshes remount
  // it (page and scroll reset), so the alert being viewed could sit on
  // another page or outside the scroll viewport. Whenever the selected alert
  // changes (or the table mounts) flip to its page, then nudge the scroll
  // container just enough to show its row under the sticky header. Manual
  // paging is respected: the page is only synced when the selection changes.
  //
  // The two memories live in different places on purpose. A remount resets the
  // scroll position, so "already scrolled to this row" is per mount and the row
  // is nudged back into view each time. It does NOT reset `page` (the parent
  // owns it), so "already synced to this selection" must be the parent's too:
  // held here, every refresh that found new alerts read as a new selection and
  // pulled an operator who had paged away back to the selected alert's page.
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
  const selectedRowKey = selectedRow ? getVideoUrlCacheKey(selectedRow) : null;
  const localPageSyncedKeyRef = React.useRef<string | null>(null);
  const lastPageSyncedKeyRef = pageSyncedKeyRef ?? localPageSyncedKeyRef;
  const lastScrolledKeyRef = React.useRef<string | null>(null);
  // Viewport anchor for the selected row: where it sat relative to the scroll
  // container the last time it was rendered (null = not on the displayed
  // page). When a background refresh prepends new alerts, the row shifts down
  // the list; we put it back at the same offset (or follow it onto the next
  // page) so the operator's view doesn't jump. Only applied when the *data*
  // changed, never for the user's own sort / paging / scrolling.
  const anchorRef = React.useRef<{ key: string | null; offset: number | null }>({
    key: null,
    offset: null,
  });
  const prevAlertDataRef = React.useRef<any>(alertData);
  const pendingAnchorRestoreRef = React.useRef(false);
  const getSelectedRowOffset = (): number | null => {
    const container = tableContainerRef.current;
    const rowEl = container?.querySelector<HTMLElement>(
      '[data-alert-row-selected="true"]'
    );
    if (!container || !rowEl) return null;
    return (
      rowEl.getBoundingClientRect().top -
      container.getBoundingClientRect().top
    );
  };
  const recordAnchor = () => {
    anchorRef.current = { key: selectedRowKey, offset: getSelectedRowOffset() };
  };

  // Floating jump back to the newest alerts (top of page 1) once the operator
  // has scrolled the list or paged away. New rows still land in the table
  // immediately; this only offers a way back to the top.
  const [showNewestAlertsButton, setShowNewestAlertsButton] =
    React.useState(false);
  const scrollingToNewestRef = React.useRef(false);
  const updateNewestAlertsButtonVisibility = React.useCallback(() => {
    const el = tableContainerRef.current;
    const scrolledAway = el
      ? shouldShowNewestAlertsButton(
          el.scrollTop,
          el.scrollHeight,
          el.clientHeight
        )
      : false;
    if (scrollingToNewestRef.current) {
      if (page === 0 && !scrolledAway) {
        scrollingToNewestRef.current = false;
      } else {
        setShowNewestAlertsButton(false);
        return;
      }
    }
    setShowNewestAlertsButton(page > 0 || scrolledAway);
  }, [page]);
  const handleTableScroll = () => {
    recordAnchor();
    updateNewestAlertsButtonVisibility();
  };
  const handleScrollToNewestAlerts = () => {
    scrollingToNewestRef.current = true;
    if (page !== 0) {
      setPage(0);
    }
    const el = tableContainerRef.current;
    if (el) {
      el.scrollTo({ top: 0, behavior: "smooth" });
    }
    setShowNewestAlertsButton(false);
  };

  const selectedSortedIndex = React.useMemo(
    () =>
      selectedRowKey
        ? sortedRows.findIndex(
          (row: any) => getVideoUrlCacheKey(row) === selectedRowKey
        )
        : -1,
    [sortedRows, selectedRowKey]
  );

  React.useLayoutEffect(() => {
    const dataChanged = prevAlertDataRef.current !== alertData;
    prevAlertDataRef.current = alertData;
    if (!selectedRowKey || selectedSortedIndex < 0) {
      return;
    }
    const targetPage = Math.floor(selectedSortedIndex / rowsPerPage);
    if (lastPageSyncedKeyRef.current !== selectedRowKey) {
      // Selection changed (keyboard / arrows / click / auto-advance).
      lastPageSyncedKeyRef.current = selectedRowKey;
      pendingAnchorRestoreRef.current = false;
      if (targetPage !== page) {
        setPage(targetPage);
        return; // re-runs once the page renders
      }
    } else if (
      (dataChanged || pendingAnchorRestoreRef.current) &&
      anchorRef.current.key === selectedRowKey &&
      anchorRef.current.offset !== null
    ) {
      // Same selection, new data (background refresh): the row was on screen
      // before, keep it exactly where it was.
      if (targetPage !== page) {
        pendingAnchorRestoreRef.current = true;
        setPage(targetPage);
        return; // re-runs once the page renders, then restores the offset
      }
      pendingAnchorRestoreRef.current = false;
      const container = tableContainerRef.current;
      const currentOffset = getSelectedRowOffset();
      if (container && currentOffset !== null) {
        const delta = currentOffset - anchorRef.current.offset;
        if (Math.abs(delta) >= 1) {
          container.scrollTop += delta;
        }
      }
      recordAnchor();
      return;
    }
    if (lastScrolledKeyRef.current !== selectedRowKey) {
      const container = tableContainerRef.current;
      const rowEl = container?.querySelector<HTMLElement>(
        '[data-alert-row-selected="true"]'
      );
      if (container && rowEl) {
        lastScrolledKeyRef.current = selectedRowKey;
        scrollAlertRowIntoView(container, rowEl);
      }
    }
    recordAnchor();
  }, [
    alertData,
    selectedRowKey,
    selectedSortedIndex,
    page,
    rowsPerPage,
    setPage,
    lastPageSyncedKeyRef,
  ]);
  React.useLayoutEffect(() => {
    updateNewestAlertsButtonVisibility();
  }, [
    updateNewestAlertsButtonVisibility,
    alertData,
    visibleRows,
    page,
    rowsPerPage,
    tableDensity,
    fillAvailableHeight,
    tableBodyHeight,
  ]);
  // Tile counts come from the same rows the table is built from, minus the
  // DoubleTake filter itself — so each tile counts what clicking it shows.
  const queueRows = React.useMemo(
    () => getRowsBeforeVlmFilter(alertData, detectionType, outcomeFilterType),
    [
      alertData,
      detectionType,
      outcomeFilterType,
      mutedAlertSites,
      mutedAlertCameras,
      userFilters?.filter_type,
    ]
  );
  const queueCounts = React.useMemo(() => getVlmQueueCounts(queueRows), [queueRows]);
  const vlmPassedCount = queueCounts.passed;

  // Auto-resolve incoming muted alerts. Only from the live (Unresolved)
  // queue — rows in the Resolved/All views are already resolved, and
  // re-submitting outcomes for them would post duplicates on every fetch.
  React.useEffect(() => {
    if (!hideMutedFromView(userFilters?.filter_type)) return;
    // Resolving is an outcome write; users without set_alert_outcome (View
    // Only) would only collect 403s (ENG-1813).
    if (!canSetAlertOutcome(userPermissions)) return;
    if (alertData.length > 0) {
      const mutedAlerts = alertData.filter((row: any) =>
        isAlertMuted(row, mutedAlertSites, mutedAlertCameras)
      );

      mutedAlerts.forEach((mutedAlert: any) => {
        handleSubmitMutedAlert(mutedAlert, navigate);
      });
    }
  }, [
    alertData,
    mutedAlertSites,
    mutedAlertCameras,
    userFilters?.filter_type,
    userPermissions,
  ]);

  React.useEffect(() => {
    handleReorderIndex(
      stableSort(alertData, getComparator(order, orderBy)),
      setAlertsIndexOrder,
      alertData,
      setAlertData,
      "alerts",
      detectionType
    );
  }, [order, visibleRows]);

  React.useEffect(() => {
    localStorage.setItem("alertsTableOrder", order);
    if (orderBy === "utc_time") {
      localStorage.setItem("alertsTableOrderBy", "clip_header_received");
    } else if (orderBy === "time") {
      localStorage.setItem("alertsTableOrderBy", "clip_received_local");
    } else if (orderBy === "site") {
      localStorage.setItem("alertsTableOrderBy", "customer_name");
    } else if (orderBy === "formatted_date") {
      localStorage.setItem("alertsTableOrderBy", "approx_capture_timestamp");
    } else {
      localStorage.setItem("alertsTableOrderBy", orderBy);
    }
    onTableSortChange?.(order, orderBy);
  }, [order, orderBy, onTableSortChange]);

  const currentSelectedRowKeyGetter = getCurrentSelectedRowKey ?? (() => null);

  const handleHasVideoToggle = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    const nextFilters = { ...userFilters, has_video: checked };
    let newNumber = Number(pageLoadedNumber);
    newNumber += 1;
    setPageLoadedNumber(newNumber);
    localStorage.setItem("pageLoadedNumber", `${newNumber}`);
    localStorage.setItem("latestAlertFilters", JSON.stringify(nextFilters));
    setPage(0);
    setFetchingFilteredData(true);
    setUserFilters(nextFilters);
    updateSearchParamValues(
      nextFilters,
      searchParams,
      setSearchParams,
      detectionType
    );
    getAlertsWithQuerry(
      setAlertData,
      navigate,
      setSelectedRow,
      setFetchingFilteredData,
      setSeeMoreURL,
      setMP4URL,
      userFilters.parent_group,
      userFilters.site,
      userFilters.camera,
      yyyymmddGenerator(userFilters.start_date),
      yyyymmddGenerator(userFilters.end_date),
      userFilters.start_time,
      userFilters.end_time,
      userFilters.filter_type,
      setSelectedButtonsFilled,
      setThumbnailVideos,
      setDateOutsideRange,
      setPage,
      setVideoClipLoading,
      setAlertsToDisplay,
      setPageLoading,
      setSelectedAlertCameraInfo,
      setIgnoreZonesSelectedAlert,
      setAlertsIndexOrder,
      setRefreshInterval,
      setCurrentName,
      setUserFilters,
      setRefreshingData,
      newNumber,
      refreshInterval,
      nextFilters,
      cameraOptions,
      siteOptions,
      setRefreshingToken,
      refreshingToken,
      setAlertLabels,
      setPeriodHours,
      setQueryLimitHit,
      setForwardArrowDisabled,
      setBackArrowDisabled,
      setShowRefreshButton,
      setRowsSortable,
      setAlertsRefreshing,
      currentSelectedRowKeyGetter,
      mutedAlertSites,
      mutedAlertCameras,
      setVideoProcessing
    );
  };

  const handleDownloadReportClick = async () => {
    if (reportDownloadUrl) {
      saveAs(reportDownloadUrl, "report.csv");
      return;
    }

    reportAbortRef.current?.abort();
    const controller = new AbortController();
    reportAbortRef.current = controller;
    const keyAtFetch = reportKey;
    try {
      setIsFetchingReport(true);
      const downloadUrl = await getAlertsDownloadURL(
        userFilters.parent_group,
        userFilters.site,
        userFilters.camera,
        yyyymmddGenerator(userFilters.start_date),
        yyyymmddGenerator(userFilters.end_date),
        userFilters.start_time,
        userFilters.end_time,
        controller.signal
      );
      if (mountedRef.current) {
        setFetchedReport({ key: keyAtFetch, url: downloadUrl });
      }
    } catch (error) {
      const name = (error as Error)?.name;
      const code = (error as { code?: string })?.code;
      if (name === "AbortError" || name === "CanceledError" || code === "ERR_CANCELED") {
        return; // user changed filters / left the page — not an error
      }
      // Surface the backend's actionable detail (e.g. "camera filters are not
      // supported for ranges older than 60 days", "alert history begins
      // 2026-04-28…") instead of a generic shrug.
      const detail = (
        error as { response?: { data?: { detail?: string } } }
      )?.response?.data?.detail;
      // Only once the archived-range feature is on: before that the old copy
      // stands, and an axios message like "Request failed with status code
      // 500" is no help to a customer.
      alert(
        showArchivedReports
          ? detail || (error as Error)?.message || "Unable to download report"
          : "Unable to download report"
      );
    } finally {
      if (mountedRef.current) {
        setIsFetchingReport(false);
      }
    }
  };

  return (
    <Paper
      className={`w-full ${
        fillAvailableHeight ? "flex h-full min-h-0 flex-col" : ""
      }`}
    >
      <div
        className={`flex shrink-0 flex-col gap-2 ${
          fillAvailableHeight ? "pt-2" : "relative top-[8px]"
        } ${isCondensed ? "pb-1" : ""}`}
      >
        {/* <div className="absolute left-[5px]">
            <MarkAsSeenButton
              alertData={alertData}
              setAlertData={setAlertData}
            />
          </div> */}
        <div className="flex items-start justify-between gap-2 px-[15px] pr-[5px] pt-3">
          <div className={alertsTableFilterRowClassName}>
            <FilterMenu
              userFilters={userFilters}
              setFetchingFilteredData={setFetchingFilteredData}
              setAlertData={setAlertData}
              navigate={navigate}
              setSelectedRow={setSelectedRow}
              setSeeMoreURL={setSeeMoreURL}
              setMP4URL={setMP4URL}
              setUserFilters={setUserFilters}
              setSelectedButtonsFilled={setSelectedButtonsFilled}
              setThumbnailVideos={setThumbnailVideos}
              setDateOutsideRange={setDateOutsideRange}
              setPage={setPage}
              setVideoClipLoading={setVideoClipLoading}
              setAlertsToDisplay={setAlertsToDisplay}
              setPageLoading={setPageLoading}
              setSelectedAlertCameraInfo={setSelectedAlertCameraInfo}
              setIgnoreZonesSelectedAlert={setIgnoreZonesSelectedAlert}
              setAlertsIndexOrder={setAlertsIndexOrder}
              setRefreshInterval={setRefreshInterval}
              currentName={currentName}
              setCurrentName={setCurrentName}
              pageLoadedNumber={pageLoadedNumber}
              setPageLoadedNumber={setPageLoadedNumber}
              setRefreshingData={setRefreshingData}
              cameraOptions={cameraOptions}
              siteOptions={siteOptions}
              setRefreshingToken={setRefreshingToken}
              refreshingToken={refreshingToken}
              setAlertLabels={setAlertLabels}
              setPeriodHours={setPeriodHours}
              setQueryLimitHit={setQueryLimitHit}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              detectionType={detectionType}
              refreshInterval={refreshInterval}
              setOutcomeFilterType={setOutcomeFilterType}
              setForwardArrowDisabled={setForwardArrowDisabled}
              setBackArrowDisabled={setBackArrowDisabled}
              setShowRefreshButton={setShowRefreshButton}
              setRowsSortable={setRowsSortable}
              setAlertsRefreshing={setAlertsRefreshing}
              getCurrentSelectedRowKey={currentSelectedRowKeyGetter}
              setVideoProcessing={setVideoProcessing}
            />
            {showOutcomeFilter && (
              <OutcomeTypeFilter
                outcomeFilterType={outcomeFilterType}
                setOutcomeFilterType={setOutcomeFilterType}
                setPage={setPage}
              />
            )}
            <DetectionTypeFilter
              alertLabels={alertLabels}
              detectionType={detectionType}
              setDetectionType={setDetectionType}
              userFilters={userFilters}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              setPage={setPage}
            />
          </div>
          {onDensityChange && (
            <AlertsDensityToggle
              density={tableDensity}
              onDensityChange={onDensityChange}
            />
          )}
        </div>
        {/* <div className="absolute left-[8px] top-[30px] text-[14px] pl-[8px] flex">
          Alerts Refreshing:
          <div className="pl-[7px]">
            {alertsRefreshing ?
              <Tooltip title="Alerts refreshing in background" placement="top">
                <div className="animate-spin">
                  <LoopIcon
                  />
                </div>
              </Tooltip>
              :
              <Tooltip title="Alerts not refreshing in background." placement="top">
                <div>
                  <SyncDisabledIcon
                  />
                </div>
              </Tooltip>
            }
          </div>
        </div> */}
        {showVlmFilters && (
          <div className="grid grid-cols-5 gap-2 px-[15px]">
            {VLM_FILTER_ORDER.map((filterOption) => {
              const isActive = vlmFilterType === filterOption;
              const label = VLM_FILTER_LABELS[filterOption];
              const count = getVlmFilterCount(queueCounts, filterOption);
              const palette = VLM_TILE_PALETTE[filterOption];
              return (
                <button
                  key={filterOption}
                  type="button"
                  aria-pressed={isActive}
                  aria-label={`${label} (${count})`}
                  className={`min-w-0 overflow-hidden rounded border px-1 py-1 text-center ${
                    isActive ? palette.active : palette.idle
                  }`}
                  onClick={() => {
                    setVlmFilterType(filterOption);
                    setPage(0);
                  }}
                >
                  <p className="font-mulish text-[16px] font-bold leading-none">
                    {count}
                  </p>
                  <p className="min-w-0 pt-[2px] font-mulish text-[9px] font-bold uppercase leading-tight tracking-normal break-words">
                    {label}
                  </p>
                </button>
              );
            })}
          </div>
        )}
        {!isCondensed && (
          <div className="pl-[15px]">
            <FormControlLabel
              sx={{
                color: "#001943",
                "& .MuiFormControlLabel-label": {
                  fontFamily: "Mulish",
                  fontSize: "12px",
                },
              }}
              label={
                <div className="flex items-center">
                  <span>Completed Alerts Only</span>
                  <Tooltip
                    title={`Selecting this option shows alerts only after their processing window has closed. If unchecked, alerts appear as soon as they are dispatched, and temporary (usually shorter) clips will be created until processing is complete.`}
                    placement="top"
                    sx={{ fontSize: "12px", fontFamily: "mulish" }}
                  >
                    <span className="ml-[4px] flex items-center">
                      <InfoOutlinedIcon
                        sx={{ fontSize: "12px" }}></InfoOutlinedIcon>
                    </span>
                  </Tooltip>
                </div>
              }
              control={
                <Checkbox
                  color="warning"
                  checked={Boolean(userFilters?.has_video)}
                  onChange={handleHasVideoToggle}
                />
              }
            />
          </div>
        )}
      </div>
      {fetchErrorMessage && alertData.length > 0 && (
        // Rows from the previous successful fetch are still on screen, so the
        // empty-state message below won't show — flag the stale data here.
        <div
          role="alert"
          className="font-mulish text-[14px] text-red-700 bg-red-50 border border-red-200 rounded px-[12px] py-[8px] mx-[8px] mb-[8px]"
        >
          {fetchErrorMessage} The list below may be out of date.
        </div>
      )}
      {showArchivedReports &&
        filtersApplied &&
        alertData.length > 0 &&
        isArchivedRange(userFilters.start_date) && (
          // ENG-1842: a range straddling the retention horizon renders only
          // its most recent ~60 days here — without this banner the older part
          // is silently missing, which reads as a bug. The report download
          // includes the full range (the backend merges the archived part from
          // the Athena mirror with the recent DynamoDB tail).
          <div
            role="status"
            className="font-mulish text-[14px] text-actuate-blue bg-blue-50 border border-blue-200 rounded px-[12px] py-[8px] mx-[8px] mb-[8px]"
          >
            Alerts older than {ALERT_RETENTION_DAYS} days are not shown in this
            list. The report download includes the full selected range.
          </div>
        )}
      <div
        className={
          fillAvailableHeight
            ? "relative flex min-h-0 flex-1 flex-col"
            : "relative"
        }
      >
        {showNewestAlertsButton && (
          <button
            type="button"
            data-testid="newest-alerts-button"
            onClick={handleScrollToNewestAlerts}
            className="absolute left-1/2 top-10 z-20 flex -translate-x-1/2 items-center gap-0.5 rounded-full border border-actuate-blue/15 bg-white px-2.5 py-1 font-mulish text-[12px] font-semibold text-actuate-blue shadow-md hover:bg-[#F5F8FC]"
          >
            <KeyboardArrowUpIcon sx={{ fontSize: 17 }} />
            Newest Alerts
          </button>
        )}
        <TableContainer
          ref={tableContainerRef}
          onScroll={handleTableScroll}
          sx={
            fillAvailableHeight
              ? { flex: 1, minHeight: 0, overflow: "auto" }
              : { height: tableBodyHeight, overflowX: "auto" }
          }
        >
        <Table
          sx={{
            fontFamily: "Mulish",
            tableLayout: "fixed",
            "& .MuiTableHead-root .MuiTableCell-root": {
              whiteSpace: "nowrap",
            },
            "& .MuiTableHead-root .MuiTableSortLabel-root": {
              whiteSpace: "nowrap",
            },
          }}
          aria-labelledby="tableTitle"
          stickyHeader={true}
          size="small"
        >
          {isCondensed ? (
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    fontFamily: "Mulish",
                    fontWeight: 700,
                    fontSize: 12,
                    backgroundColor: "#fff",
                    borderBottom: "1px solid #E5E5E5",
                  }}
                >
                  {`${getCondensedWindowLabel({
                    periodHours,
                    userFilters,
                  })} · ${countUnresolvedAlerts(filteredRows)} unresolved`}
                </TableCell>
              </TableRow>
            </TableHead>
          ) : rowsSortable ? (
            <EnhancedAlertsTableHead
              onRequestSort={handleRequestSort}
              orderBy={orderBy}
              order={order}
              showVlmVerdictColumn={showVlmFilters}
            />
          ) : (
            <TableHead>
              <TableRow>
                <TableCell sx={alertsTableSeenColumnSx} />
                {[
                  "Site",
                  "Camera",
                  "Site's Time",
                  "Type",
                  "Detection",
                  ...(showVlmFilters ? ["DT Verdict"] : []),
                ].map((title: string) => {
                    return (
                      <TableCell
                        key={title}
                        align="left"
                        style={{
                          width: `20%`,
                        }}
                        sx={{
                          fontFamily: "mulish",
                          fontWeight: 700,
                          fontSize: 10,
                        }}
                      >
                        {title === "Site's Time" ? (
                          <div className="flex shrink-0 whitespace-nowrap">
                            <>{`Site Time`}</>
                            <div>
                              <IconButton
                                aria-label="more"
                                id="long-button"
                                aria-controls={open ? "long-menu" : undefined}
                                aria-expanded={open ? "true" : undefined}
                                aria-haspopup="true"
                                onClick={handleClick}
                                sx={{
                                  width: "18px",
                                  height: "18px",
                                  padding: "0px",
                                }}
                              >
                                <MoreVertIcon sx={{ fontSize: "18px" }} />
                              </IconButton>
                              <Menu
                                id="long-menu"
                                MenuListProps={{
                                  "aria-labelledby": "long-button",
                                }}
                                anchorOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                slotProps={{
                                  paper: {
                                    style: {
                                      maxHeight: 48 * 4.5,
                                      width: "20ch",
                                    },
                                  },
                                }}
                              >
                                <MenuItem
                                  key="mm-dd-yyyy"
                                  onClick={(event) => {
                                    setDateMMDDYYYY(true);

                                    event.stopPropagation(); // Stop event propagation to prevent table sorting
                                    handleClose(event);
                                  }}
                                >
                                  MM/DD/YYYY
                                </MenuItem>
                                <MenuItem
                                  key="dd-mm-yyyy"
                                  onClick={(event) => {
                                    setDateMMDDYYYY(false);
                                    event.stopPropagation(); // Stop event propagation to prevent table sorting
                                    handleClose(event);
                                  }}
                                >
                                  DD/MM/YYYY
                                </MenuItem>
                              </Menu>
                            </div>
                          </div>
                        ) : (
                          title
                        )}
                      </TableCell>
                    );
                })}
              </TableRow>
            </TableHead>
          )}
          {alertData.length === 0 && (
            <TableBody>
              {/* Empty-state prompt (restored — the original lived in the
                  retired AlertsMirror.tsx): without date/time filters the
                  backend only returns the last ~2 hours, so an empty result
                  needs to say so or the blank table reads as a bug. */}
              <TableRow>
                <TableCell colSpan={99} align="center" sx={{ border: 0 }}>
                  {fetchErrorMessage ? (
                    <p
                      role="alert"
                      className="font-mulish text-[18px] pt-[60px] pb-[20px] text-center text-red-700"
                    >
                      {fetchErrorMessage}
                    </p>
                  ) : showArchivedReports &&
                    filtersApplied &&
                    isArchivedRange(userFilters.start_date) ? (
                    /* ENG-1842: rows past the retention window are gone from
                       DynamoDB, so the grid is empty by design — say so and
                       route to the Athena-backed report instead of looking
                       like a bug. Media is genuinely unavailable at this age
                       (frames expire at 14 days, MP4s archive at 60).
                       filtersApplied keeps the CTA from flashing during the
                       initial fetch, when alertData is empty while loading. */
                    <div className="pt-[60px] pb-[20px] text-center">
                      <p role="status" className="font-mulish text-[18px]">
                        Alerts older than {ALERT_RETENTION_DAYS} days are
                        available as a report download only; media is no longer
                        available.
                      </p>
                      {reportCameraFilterActive ? (
                        /* The backend refuses camera filters for archived
                           ranges (the mirror has no camera ids) — offering the
                           button here is a guaranteed dead end. */
                        <p className="font-mulish pt-[8px] text-[14px]">
                          Camera filters are not available for archived ranges —
                          clear the camera filter to fetch the report.
                        </p>
                      ) : (
                        <OrangeTextButton
                          onClick={handleDownloadReportClick}
                          disabled={isFetchingReport}
                          text={
                            isFetchingReport
                              ? "Generating report… this can take a few minutes"
                              : reportDownloadUrl
                              ? "Download Report"
                              : "Fetch Report"
                          }
                        />
                      )}
                    </div>
                  ) : scopeEmptyCopy ? (
                    <div className="pt-[60px] pb-[20px] text-center">
                      <p role="status" className="font-mulish text-[18px]">
                        {scopeEmptyCopy}
                      </p>
                      {showTroubleshootingPage && (
                        <p className="font-mulish pt-[8px]">
                          <Link
                            to="/troubleshooter"
                            className="font-mulish text-[16px] text-actuate-orange underline"
                          >
                            Check delivery
                          </Link>
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="font-mulish text-[18px] pt-[60px] pb-[20px] text-center">
                      {!userFilters.start_date &&
                      !userFilters.end_date &&
                      !userFilters.start_time &&
                      !userFilters.end_time
                        ? "No recent alerts over the past 2 hours. Please apply date filters to see past alerts."
                        : "No alerts to display for the selected filters."}
                    </p>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          )}
          {alertData.length > 0 && (
            <TableBody>
              {visibleRows.map((row: any, index: number) => {
                const isRowSelected =
                  selectedRow &&
                  getVideoUrlCacheKey(selectedRow) === getVideoUrlCacheKey(row);
                const rowResolved = isAlertResolved(row);
                let sx: any = { cursor: "pointer" };
                if (!row.seen && !rowResolved && !isRowSelected) {
                  sx = {
                    cursor: "pointer",
                    backgroundColor: "rgba(255, 132, 0, 0.08)",
                  };
                }
                if (isRowSelected) {
                  sx = {
                    cursor: "pointer",
                    backgroundColor: "rgba(2, 136, 209, 0.2)",
                  };
                }
                return (
                  <React.Fragment key={`${row.formatted_date}_${index}`}>
                    <ThemeProvider theme={theme}>
                      <TableRow
                        hover
                        onClick={() => {
                          handleRowSelected(
                            row,
                            alertData,
                            setAlertData,
                            setSelectedButtonsFilled,
                            setSeeMoreURL,
                            navigate,
                            setMP4URL,
                            setVideoClipLoading,
                            setSelectedRow,
                            setSelectedAlertCameraInfo,
                            setIgnoreZonesSelectedAlert,
                            refreshingToken,
                            visibleRows,
                            setBackArrowDisabled,
                            setForwardArrowDisabled,
                            setShowRefreshButton,
                            currentSelectedRowKeyGetter,
                            setVideoProcessing,
                            useAsyncGeneration,
                            setSelectedRowRef
                          );
                          scrollToTop();
                        }}
                        tabIndex={-1}
                        key={row.id}
                        sx={sx}
                        data-alert-row-selected={isRowSelected ? "true" : undefined}
                      >
                        {isCondensed ? (
                          <TableCell
                            sx={{
                              fontFamily: "Mulish",
                              padding: 0,
                              borderBottom: "1px solid #E5E5E5",
                            }}
                          >
                            <div className="flex">
                              {rowResolved ? (
                                <div className="flex w-7 shrink-0 items-center justify-center">
                                  {createDetctionTypeIcon(
                                    row.detection_type,
                                    row.engineering_outcome?.outcome_v2,
                                    row.dispatch_muted === true,
                                    isAlertWithheldFromReceiver(row)
                                  )}
                                </div>
                              ) : (
                                <div
                                  className="w-1 shrink-0"
                                  style={{
                                    backgroundColor: getAlertTypeStripeColor(
                                      row.alert_labels
                                    ),
                                  }}
                                />
                              )}
                              <div className="min-w-0 flex-1 px-2 py-1.5">
                                <div className="flex items-start justify-between gap-2">
                                  <TruncatedTextWithTooltip
                                    text={`${row.display_name || row.customer_name || ""} · ${row.camera_name || ""}`}
                                    className="min-w-0 flex-1 truncate font-mulish text-[12px] font-bold text-actuate-blue"
                                  />
                                  <p className="shrink-0 font-mulish text-[11px] text-actuate-blue">
                                    {formatDateBasedOnPreference(
                                      row.formatted_date,
                                      dateMMDDYYYY
                                    )}
                                  </p>
                                </div>
                                <div className="flex flex-wrap items-center gap-1 pt-1">
                                  <AlertTypeChip
                                    alertLabels={row.alert_labels}
                                  />
                                  {showVlmFilters && <VlmVerdictChip row={row} />}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        ) : (
                          <>
                            <TableCell sx={alertsTableSeenColumnSx}>
                              {!row.seen && !rowResolved && (
                                <FiberManualRecordIcon
                                  fontSize="small"
                                  color="warning"
                                  sx={{ display: "block", fontSize: 14 }}
                                />
                              )}
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={alertsTableSiteCameraCellSx}
                            >
                              {row.display_name
                                ? row.display_name
                                : row.customer_name}
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={alertsTableSiteCameraCellSx}
                            >
                              {row.camera_name}
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontSize: "11.5px", fontFamily: "mulish" }}
                            >
                              {formatDateBasedOnPreference(
                                row.formatted_date,
                                dateMMDDYYYY
                              )}
                              &nbsp;
                              <Tooltip
                                title={row.timezone}
                                placement="top"
                                sx={{ fontSize: "11.5px", fontFamily: "mulish" }}
                              >
                                <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                              </Tooltip>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontSize: "11.5px", fontFamily: "mulish" }}
                            >{`${handleCreateAlertTypeNoUnderscore(
                              row.alert_labels
                            )}`}</TableCell>
                            <TableCell align="left">
                              {createDetctionTypeIcon(
                                row.detection_type,
                                row.engineering_outcome?.outcome_v2,
                                row.dispatch_muted === true,
                                isAlertWithheldFromReceiver(row)
                              )}
                            </TableCell>
                            {showVlmFilters && (
                              <TableCell align="left">
                                <VlmVerdictCell row={row} />
                              </TableCell>
                            )}
                          </>
                        )}
                        {/* <TableCell>
                        {row.detection_type && (
                          <OrangeTextButton
                            text="See Notes"
                            onClick={() => {
                              setNoteText(row.notes);
                              setNotesDialogOpen(true);
                            }}
                            disabled={false}
                          />
                        )}
                      </TableCell> */}
                      </TableRow>
                      {/* </Tooltip> */}
                    </ThemeProvider>
                  </React.Fragment>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  {/* Condensed is a single column: a wider colSpan here adds
                      phantom columns to the fixed-layout table and squeezes
                      every row into the first one on a short last page. */}
                  <TableCell
                    colSpan={isCondensed ? 1 : showVlmFilters ? 7 : 6}
                  />
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
        </TableContainer>
      </div>
      <div className="shrink-0 font-mulish">
        {/* Outer columns size to content: with 1fr/1fr the left column was
            forced as wide as the pagination block, which pushed the page
            arrows outside the card so users had to scroll to reach them. */}
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-1 px-1 py-0.5">
          <div className="justify-self-start">
            <IconButton
              aria-label="Table actions"
              onClick={(event) => setFooterMenuAnchor(event.currentTarget)}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={footerMenuAnchor}
              open={footerMenuOpen}
              onClose={() => setFooterMenuAnchor(null)}
            >
              <MenuItem
                disabled={isFetchingReport}
                onClick={() => {
                  handleDownloadReportClick();
                  setFooterMenuAnchor(null);
                }}
                sx={{ fontFamily: "Mulish" }}
              >
                {isFetchingReport
                  ? "Fetching Report"
                  : reportDownloadUrl
                  ? "Download Report"
                  : "Fetch Report"}
              </MenuItem>
              {viewAlertMuting && (
                <MenuItem
                  onClick={() => {
                    setSeeMutedSiteAndCamerasDialogOpen(true);
                    setFooterMenuAnchor(null);
                  }}
                  sx={{ fontFamily: "Mulish" }}
                >
                  See Muted Sites & Cameras
                </MenuItem>
              )}
            </Menu>
          </div>
          <div className="justify-self-center text-actuate-blue">
            {alertsRefreshing ? (
              <Tooltip title="Alerts refreshing in background" placement="top">
                <div className="animate-spin">
                  <LoopIcon fontSize="small" />
                </div>
              </Tooltip>
            ) : (
              <Tooltip
                title="Alerts not refreshing in background."
                placement="top"
              >
                <div>
                  <SyncDisabledIcon fontSize="small" />
                </div>
              </Tooltip>
            )}
          </div>
          <div className="min-w-0 justify-self-end">
            <TablePagination
              // ENG-1489 hides the picker in condensed mode only; detailed
              // mode keeps the SUP-510 rows-per-page control.
              rowsPerPageOptions={
                isCondensed ? [] : ALERTS_ROWS_PER_PAGE_OPTIONS
              }
              component="div"
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={compactTablePaginationSx}
            />
          </div>
        </div>
        {!isCondensed &&
          !userFilters?.start_date &&
          !userFilters?.end_date && (
            <p className="px-[15px] pb-2 font-mulish text-[12px]">
              {alertData.length === 100
                ? periodHours === 1
                  ? `Displaying ${
                      detectionType === "All" ? 100 : ""
                    } most recent alerts from the past hour.`
                  : `Displaying ${
                      detectionType === "All" ? 100 : ""
                    } most recent alerts from the past ${periodHours} hours.`
                : periodHours === 1
                ? "Displaying alerts from the past hour."
                : `Displaying alerts from the past ${periodHours} hours.`}
            </p>
          )}
      </div>
    </Paper>
  );
};
